import Phaser from 'phaser';
import { TileData, BuildingData } from '../socket/SocketManager';

const MINIMAP_WIDTH = 100;
const MINIMAP_HEIGHT = 80;

const MINI_TILE_COLORS: Record<string, number> = {
  ROAD: 0x444444,
  SIDEWALK: 0x888888,
  GROUND: 0x5a8a3c,
  PARK: 0x4caf50,
};

const MINI_BUILDING_COLORS: Record<string, number> = {
  HOUSE: 0xc8a882,
  APARTMENT: 0x9b8b7a,
  SHOP: 0xf4c842,
  POLICE_STATION: 0x3a6bc4,
  WAREHOUSE: 0x7a5c3a,
  PARK: 0x4caf50,
  CASINO: 0x7b2fbe,
  DEAL_POINT: 0x4a4a4a,
  WHOLESALER: 0x8b4513,
};

export class Minimap {
  private graphics: Phaser.GameObjects.Graphics;
  private cameraRect: Phaser.GameObjects.Graphics;
  private gridWidth: number;
  private gridHeight: number;

  constructor(
    private scene: Phaser.Scene,
    tiles: TileData[],
    buildings: BuildingData[],
    gridWidth: number,
    gridHeight: number,
  ) {
    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;

    const { width: screenW, height: screenH } = scene.scale;
    const baseX = screenW - MINIMAP_WIDTH - 10;
    const baseY = screenH - MINIMAP_HEIGHT - 10;

    // Background
    this.graphics = scene.add.graphics();
    this.graphics.setScrollFactor(0);
    this.graphics.setDepth(1001);

    this.graphics.fillStyle(0x000000, 0.7);
    this.graphics.fillRect(baseX - 2, baseY - 2, MINIMAP_WIDTH + 4, MINIMAP_HEIGHT + 4);

    const tileW = MINIMAP_WIDTH / gridWidth;
    const tileH = MINIMAP_HEIGHT / gridHeight;

    // Draw tiles
    for (const tile of tiles) {
      const color = MINI_TILE_COLORS[tile.tileType] ?? 0x5a8a3c;
      this.graphics.fillStyle(color, 1);
      this.graphics.fillRect(
        baseX + tile.gridX * tileW,
        baseY + tile.gridY * tileH,
        Math.ceil(tileW),
        Math.ceil(tileH),
      );
    }

    // Draw buildings
    for (const b of buildings) {
      const color = MINI_BUILDING_COLORS[b.type] ?? 0x888888;
      this.graphics.fillStyle(color, 1);
      this.graphics.fillRect(
        baseX + b.gridX * tileW,
        baseY + b.gridY * tileH,
        Math.ceil(tileW),
        Math.ceil(tileH),
      );
    }

    // Camera viewport indicator
    this.cameraRect = scene.add.graphics();
    this.cameraRect.setScrollFactor(0);
    this.cameraRect.setDepth(1002);
  }

  updateCameraPosition(
    camX: number,
    camY: number,
    camWidth: number,
    camHeight: number,
    worldWidth: number,
    worldHeight: number,
  ): void {
    const { width: screenW, height: screenH } = this.scene.scale;
    const baseX = screenW - MINIMAP_WIDTH - 10;
    const baseY = screenH - MINIMAP_HEIGHT - 10;

    this.cameraRect.clear();
    this.cameraRect.lineStyle(1, 0xff6b35, 1);

    const scaleX = MINIMAP_WIDTH / worldWidth;
    const scaleY = MINIMAP_HEIGHT / worldHeight;

    const rx = baseX + camX * scaleX;
    const ry = baseY + camY * scaleY;
    const rw = camWidth * scaleX;
    const rh = camHeight * scaleY;

    this.cameraRect.strokeRect(rx, ry, rw, rh);
  }

  getGameObjects(): Phaser.GameObjects.GameObject[] {
    return [this.graphics, this.cameraRect];
  }
}
